import { Doughnut, Line } from "react-chartjs-2";
import { Container, Typography, Box, Skeleton } from "@mui/material";
import React from "react";
import Loading from "../components/Loading";
import { useState } from "react";
import MessageModal from "../components/MessageModal";
import { DataGrid } from "@mui/x-data-grid";
import { darken, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { URL, origin } from "../config";
import { Helmet } from "react-helmet-async";

const green = "linear-gradient(315deg, #00b712 0%, #5aff15 74%)";
const red = "linear-gradient(147deg, #f71735 0%, #db3445 74%)";

export default function SwyftxDashboard() {
    const theme = useTheme();

    const formatter = Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "AUD",
    });
    const lineOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                labels: {
                    color: theme.palette.primary.contrastText,
                },
            },
            title: {
                display: true,
                text: "Swyftx account value over time",
                color: theme.palette.primary.contrastText,
            },
        },
        scales: {
            y: {
                grid: {
                    color: theme.palette.primary.contrastText,
                },
                ticks: {
                    color: theme.palette.primary.contrastText,
                },
            },
            x: {
                ticks: {
                    color: theme.palette.primary.contrastText,
                },
            },
        },
    };

    const dougnutOptions = {
        plugins: {
            title: {
                display: true,
                text: "Swyftx portfolio breakdown",
                color: theme.palette.primary.contrastText,
            },
            legend: {
                display: true,
                labels: {
                    color: theme.palette.primary.contrastText,
                },
            },
        },
    };
    const [data, setData] = useState({});
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [totalData, setTotalData] = useState();
    const [lastUpdated, setlastUpdated] = useState();
    const [gridData, setGridData] = useState([]);
    const navigate = useNavigate();
    const [lineData, setLineData] = useState({});

    console.log(gridData);

    const gridHeaders = [
        {
            field: "coin",
            headerName: "Coin",
            flex: 1,
        },
        {
            field: "value",
            type: "number",
            headerName: "Value (AUD)",
            flex: 1,
            align: "left",
            headerAlign: "left",
            valueGetter: (params) => {
                return params.row.value;
            },
            valueFormatter: (params) => {
                return formatter.format(params.value);
            },
        },
        {
            field: "change",
            headerName: "24h change",
            type: "number",
            valueGetter: (params) => {
                return params.row.change !== null
                    ? parseFloat(params.row.change)
                    : null;
            },
            valueFormatter: (params) => {
                return params.value !== null
                    ? params.value === 0
                        ? "--"
                        : `${params.value}%`
                    : "N/A";
            },
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            background:
                                params.value > 0
                                    ? green
                                    : params.value === null
                                    ? "black"
                                    : params.value === 0
                                    ? "grey"
                                    : red,
                            padding: 2,
                            borderRadius: "5px",
                            minWidth: "50px",
                        }}
                    >
                        <span>
                            {params.value !== null
                                ? params.value === 0
                                    ? "--"
                                    : `${params.value > 0 ? "+" : ""}${
                                          params.value
                                      }%`
                                : "N/A"}
                        </span>
                    </div>
                );
            },
        },
    ];

    function fetchData() {
        setLoading(true);
        fetch(`${URL}/fetch/swyftx`, {
            method: "GET",
            mode: "cors",
            credentials: "include",
            headers: {
                "content-type": "application/json",
                Origin: `${origin}`,
            },
        })
            .then((response) => {
                if (response.ok === false) {
                    navigate(`/${response.status}`);
                    setError(response.statusText);
                    throw new Error(response.statusText);
                }
                return response.json();
            })
            .then((data) => {
                var formattedData = createData(data);
                setTotalData(
                    formatter.format(data.data.accountInfo.totalValue)
                );
                setData(formattedData);
                createLineData(data);
                setlastUpdated(
                    new Date(data.lastUpdatedOn).toLocaleString("en-US", {
                        weekday: "long",
                        month: "long",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                        hour12: true, // 12-hour time format
                    })
                );
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
            });
    }

    React.useEffect(() => {
        fetchData();
    }, []);

    const closeAlert = () => {
        setError("");
    };

    function createColors(str) {
        let r = 0,
            b = 0,
            g = 0;

        for (let i = 0; i < str.length; i++) {
            let code = str.charCodeAt(i);

            r = (r + code * (i * 400)) % 256;
            g = (g + code * 120) % 256;
            b = (b + code * (i * 250)) % 256;
        }

        return "rgb(" + r + "," + g + "," + b + ")";
    }

    function createData(data) {
        const colors = [];

        const wallet = {};

        for (const asset in data.data.accountInfo.assetsValue) {
            wallet[asset] = data.data.accountInfo.assetsValue[asset];
        }

        const walletEntries = Object.entries(wallet);
        setGridData(
            walletEntries.map((x, index) => {
                const res = {};
                res["id"] = index;
                res["coin"] = x[0];
                res["value"] = x[1].value;
                res["change"] = x[1].dailyChange;
                return res;
            })
        );
        const ticks = Object.keys(wallet);

        for (let i in ticks) {
            colors.push(createColors(ticks[i]));
        }

        const graph = {
            labels: Object.keys(wallet),
            datasets: [
                {
                    label: "Value AUD",
                    data: Object.values(wallet),
                    backgroundColor: colors,
                    borderColor: colors,
                    borderWidth: 1,
                },
            ],
        };
        return graph;
    }

    function createLineData(data) {
        const totalArray = data.totalHistory.filter((x) => x.swyftx);
        const lineData = totalArray.map((x) => {
            const a = {};
            a["value"] = x.swyftx.total;
            a["timestamp"] = x["timestamp"];
            return a;
        });

        const result = lineData.reduce((a, b) => {
            a[b.timestamp.slice(0, 10)] = b.value;
            return a;
        }, {});
        const graph = {
            labels: Object.keys(result),
            datasets: [
                {
                    label: "account value",
                    data: Object.values(result),
                    backgroundColor: "rgba(42, 194, 245, 0.2)",
                    borderColor: "rgba(42, 194, 245)",
                    color: "white",
                    fill: true,
                },
            ],
        };
        setLineData(graph);
    }

    return (
        <>
            <Helmet>
                <title>WealthWatch | Swyftx</title>
            </Helmet>
            <Container
                maxWidth="xl"
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(12, 1fr)",
                    gridAutoRows: "100px",
                    gap: "20px",
                    "@media (max-width: 75em)": {
                        flexDirection: "column",
                        display: "flex",
                    },
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    sx={{
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: 1,
                        my: 2,
                        gridColumn: "span 6",
                        gridRow: "span 8",
                        outline: `1px solid ${theme.palette.primary.main}`,
                        transition: "outline 1s ease",
                        boxShadow: 14,
                        "&:hover": {
                            outline: `1px solid ${theme.palette.primary.contrastText}`,
                        },
                    }}
                >
                    <Container sx={{}}>
                        {loading ? (
                            <Loading />
                        ) : (
                            <Doughnut
                                data={data}
                                options={dougnutOptions}
                            ></Doughnut>
                        )}
                    </Container>
                    <Container
                        sx={{
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            my: 2,
                        }}
                    >
                        <Typography
                            sx={{ marginBottom: 1 }}
                            color="highlight"
                            variant="h5"
                            textAlign="center"
                        >{`Total value: ${totalData}`}</Typography>
                        <Typography
                            color="grey"
                            width="90%"
                        >{`last updated on: ${lastUpdated}`}</Typography>
                    </Container>
                </Box>
                <Box
                    sx={{
                        width: "100%",
                        marginTop: 2,
                        gridColumn: "span 6",
                        gridRow: "span 4",
                        borderRadius: 1,
                        overflow: "auto",
                        outline: `1px solid ${theme.palette.primary.main}`,
                        transition: "outline 1s ease",
                        boxShadow: 14,
                        "&:hover": {
                            outline: `1px solid ${theme.palette.primary.contrastText}`,
                        },
                    }}
                >
                    <DataGrid
                        loading={loading}
                        rows={gridData}
                        columns={gridHeaders}
                        disableRowSelectionOnClick
                        sx={gridData.reduce(
                            (total, cur) => {
                                const x = total;
                                x[`& .${cur.coin}`] = {
                                    backgroundColor: createColors(cur.coin),
                                    color: "white",
                                    "&:hover": {
                                        backgroundColor: darken(
                                            createColors(cur.coin),
                                            0.3
                                        ),
                                    },
                                };
                                return x;
                            },
                            {
                                border: "none",
                                "& .MuiDataGrid-columnHeaders": {
                                    backgroundColor: theme.palette.primary.main,
                                    borderBottom: "none",
                                    color: theme.palette.primary.contrastText,
                                },
                                "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within":
                                    {
                                        outline: "none !important",
                                    },
                                "& .MuiDataGrid-footerContainer": {
                                    backgroundColor: theme.palette.primary.main,
                                    color: "white", //dosent work
                                    borderTop: "none",
                                },
                                "& .MuiDataGrid-sortIcon": {
                                    opacity: 1,
                                    color: theme.palette.primary.contrastText,
                                },
                                "& .MuiDataGrid-menuIconButton": {
                                    opacity: 1,
                                    color: theme.palette.primary.contrastText,
                                },
                                boxShadow: 14,
                            }
                        )}
                        getRowClassName={(params) => params.row.coin}
                    ></DataGrid>
                </Box>
                <Box
                    gridRow="span 4"
                    gridColumn="span 6"
                    sx={{
                        backgroundColor: "primary.main",
                        marginBottom: 2,
                        borderRadius: 1,
                        boxShadow: 14,
                        outline: `1px solid ${theme.palette.primary.main}`,
                        transition: "outline 1s ease",
                        boxShadow: 14,
                        "&:hover": {
                            outline: `1px solid ${theme.palette.primary.contrastText}`,
                        },
                        "@media (max-width: 75em)": {
                            minHeight: "40vh",
                        },
                    }}
                >
                    {loading ? (
                        <Skeleton />
                    ) : (
                        <Line data={lineData} options={lineOptions} />
                    )}
                </Box>
                <MessageModal
                    severity="error"
                    title="error"
                    open={error ? true : false}
                    onClose={closeAlert}
                >
                    {error}
                </MessageModal>
            </Container>
        </>
    );
}
